/**
 * Sourced threat intelligence (MITRE, etc.) — never LLM-generated.
 * @param {{ references?: Array<{ id?: string, source?: string, title?: string, shortDescription?: string, url?: string, sourceVersion?: string, retrievedAt?: string }>, defaultOpen?: boolean, variant?: 'dashboard' | 'report' }} props
 */
export default function ThreatContextBlock({ references = [], defaultOpen = false, variant = "dashboard" }) {
  const list = Array.isArray(references) ? references : [];
  if (!list.length) return null;

  const labelForSource = (s) => {
    const x = String(s || "").trim();
    const u = x.toUpperCase();
    if (u === "MITRE-ATTACK") return "Attack techniques";
    if (u === "CISA-KEV") return "Known exploited vulnerabilities";
    if (u === "OKTA-ADVISORIES") return "Vendor advisories";
    if (u === "OWASP-CURATED") return "OWASP guidance";
    if (u === "VERIZON-DBIR") return "Industry statistics (Verizon DBIR)";
    if (u === "MITRE-D3FEND") return "Recommended defenses";
    if (u === "NIST-NVD") return "Vulnerabilities (NVD)";
    return x || "References";
  };

  /** @type {Record<string, Array<any>>} */
  const bySource = {};
  for (const r of list) {
    const key = r?.source != null ? String(r.source) : "OTHER";
    if (!bySource[key]) bySource[key] = [];
    bySource[key].push(r);
  }

  const sourceFooter = (() => {
    const seen = new Map();
    for (const r of list) {
      const s = r?.source != null ? String(r.source) : "";
      if (!s) continue;
      if (!seen.has(s)) {
        const v = r?.sourceVersion != null ? String(r.sourceVersion) : "";
        const t = r?.retrievedAt != null ? String(r.retrievedAt) : "";
        seen.set(s, { source: s, sourceVersion: v, retrievedAt: t });
      }
    }
    const arr = [...seen.values()];
    if (!arr.length) return null;
    return (
      <div
        style={{
          marginTop: "10px",
          fontSize: "11px",
          color: variant === "report" ? "var(--idaxis-print-muted, #334155)" : "var(--text-dim, #94a3b8)",
          lineHeight: 1.35,
        }}
      >
        Sources:{" "}
        {arr
          .map((x) => `${x.source}${x.sourceVersion ? ` · ${x.sourceVersion}` : ""}${x.retrievedAt ? ` · ${x.retrievedAt.slice(0, 10)}` : ""}`)
          .join("  |  ")}
      </div>
    );
  })();

  const inner = (
    <div style={{ marginTop: "10px" }}>
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: variant === "report" ? "var(--idaxis-print-muted, #334155)" : "var(--text-dim, #94a3b8)",
          marginBottom: "8px",
        }}
      >
        THREAT CONTEXT
      </div>
      {Object.entries(bySource).map(([source, refs]) => (
        <div key={source} style={{ marginTop: "10px" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: variant === "report" ? "#0b1b34" : "var(--text-primary)",
              marginBottom: "6px",
            }}
          >
            {labelForSource(source)} ({refs.length})
          </div>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {refs.map((ref) => (
              <li
                key={`${ref.source || ""}-${ref.id || ref.url || ref.title}`}
                style={{
                  border: variant === "report" ? "1px solid var(--idaxis-print-border, #e5e7eb)" : "1px solid rgba(148,163,184,0.35)",
                  borderRadius: "6px",
                  padding: "8px 10px",
                  background: variant === "report" ? "#f8fafc" : "rgba(15,23,42,0.35)",
                }}
              >
                <div
                  style={{ fontWeight: 600, fontSize: "12px", color: variant === "report" ? "#0b1b34" : "var(--text-primary)" }}
                >
                  {ref.id ? `${ref.id}: ` : ""}
                  {ref.title || "Reference"}
                </div>
                {ref.shortDescription ? (
                  <p
                    style={{
                      margin: "6px 0 0",
                      fontSize: "12px",
                      lineHeight: 1.45,
                      color: variant === "report" ? "var(--idaxis-print-muted, #334155)" : "var(--text-secondary)",
                    }}
                  >
                    {ref.shortDescription}
                  </p>
                ) : null}
                {ref.url ? (
                  <div style={{ marginTop: "8px" }}>
                    <a
                      href={ref.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "var(--accent, #30f4e6)",
                        textDecoration: "none",
                      }}
                    >
                      View source
                    </a>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {sourceFooter}
    </div>
  );

  if (variant === "report") {
    return inner;
  }

  return (
    <details open={defaultOpen} style={{ marginTop: "10px" }}>
      <summary
        style={{
          cursor: "pointer",
          fontSize: "12px",
          fontWeight: 600,
          color: "var(--text-secondary)",
          listStyle: "none",
        }}
      >
        Threat context ({list.length})
      </summary>
      {inner}
    </details>
  );
}
